import React, {useState} from 'react';
import { Container, TitleMain, ContainerChart, Subtitle } from '../../components/AppComponents';
import HighchartsReact from 'highcharts-react-official'
import Highcharts from 'highcharts'
import { figure75 } from '../../graphs/fr/figure75';
import { figure76 } from '../../graphs/fr/figure76';
import Modal from "../../components/Modal";


const MeasuringProductiveSustainableAgriculture = () => {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState(false);

    const handleClickOpen = (value) => {
        setOpen(true);
        setText(value);
    };

    const handleClose = (value) => {
        setOpen(false);
        setText(value);
    };
    return (
        <React.Fragment>
            <Modal
                text={text}
                open={open}
                onClose={handleClose}
            />
            <Container>
                <TitleMain title="Mesurer le caractère productif et durable d’une agriculture" />
                <p>Depuis que la méthode de calcul de l’indicateur 2.4.1 des ODD sur la productivité et la durabilité de l’agriculture a été validée en mars 2019, la FAO s’est mobilisée pour aider les pays à renforcer leurs capacités afin qu’ils puissent communiquer régulièrement des données sur cet indicateur. Elle a organisé de nombreux ateliers de formation et formations bilatérales en 2019 et, en raison de la pandémie de covid-19, proposé quatre formations en ligne en 2020-2021, couvrant plus de 100 pays dans toutes les régions du monde. Toujours pour faciliter la transmission de données par les pays, la FAO a aussi publié en 2019 un recueil de documents méthodologiques clés <span className="ref" onClick={() => handleClickOpen('<b>FAO.</b> 2022. <em>Methodological documents and supporting material for SDG Indicator 2.4.1. Proportion of agricultural area under productive and sustainable agriculture.</em> Rome, FAO. <a href="https://www.fao.org/3/cb6372en/cb6372en.pdf">https://www.fao.org/3/cb6372en/cb6372en.pdf</a>')}>(FAO, 2022)</span>, ainsi qu’un cours de téléformation sur l’indicateur (disponible en anglais, espagnol et français).</p>
                <p>Ces initiatives ont aidé une quarantaine de pays à communiquer des données partielles sur l’indicateur 2.4.1 des ODD, mais seul un très petit nombre de pays ont transmis des données complètes. Une multitude de facteurs explique cette situation, notamment la complexité intrinsèque de l’indicateur, la difficulté d’exploiter d’autres sources de données, la faible fréquence des enquêtes agricoles dans les pays (qui a encore pâti de la pandémie de covid-19), mais aussi le peu de moyens techniques et financiers disponibles pour inclure le module 2.4.1 dans de nouvelles enquêtes agricoles.</p>
                <p>La pénurie actuelle de données sur l’indicateur 2.4.1 crée un manque extrêmement préjudiciable au suivi des ODD. Cet indicateur vise à mesurer la durabilité de l’agriculture, qui constitue un élément central du Programme de développement durable à l’horizon 2030. Il s’est retrouvé encore davantage mis en avant dans le discours international ces derniers mois, notamment au Sommet sur les systèmes alimentaires, à la Conférence des Nations Unies sur les changements climatiques (COP26) et à la réunion Stockholm+50. C’est pourquoi, pour le rapport de suivi de cette année sur les progrès des indicateurs des ODD, la FAO a décidé de tenter de combler ce manque et d’utiliser provisoirement une autre mesure pour rendre compte des progrès réalisés au regard de la cible 2.4 des ODD. Cette mesure supplétive se compose d’un ensemble de huit grandeurs établies, ayant un lien avec la durabilité et la productivité dans l’agriculture, et fondées sur des statistiques nationales largement disponibles.</p>
                <p>L’indicateur supplétif repose sur une méthode novatrice basée sur le cadre d’analyse Progrès accomplis vers une agriculture durable (PROSA) <span className="ref" onClick={() => handleClickOpen('<b>Ignaciuk, A., Ilicic, J., Asprooth, L., Sitko, N.J., Bernard, A., Maggio, G., Tubiello, F.N. &amp; Mueller, M.</b> 2021. Progress towards sustainable agriculture – Drivers of change. FAO Agricultural Development Economics Technical Study No. 13. Rome, FAO. <a href="https://doi.org/10.4060/cb7896en">https://doi.org/10.4060/cb7896en</a>')}>(Ignaciuk <i>et al.</i>, 2021)</span> lancé par la FAO en 2021. Ce cadre synthétise les informations des huit grandeurs pour produire une note globale à la fois de la <em>tendance</em> observée et de l’<em>état d’avancement actuel</em> par rapport à la cible d’une agriculture productive et durable. L’indicateur supplétif pourra fournir une bonne indication des progrès réalisés par les pays jusqu’à ce que ces derniers soient capables de produire l’indicateur 2.4.1 des ODD. Les résultats de cette première évaluation sont présentés à la figure 75 et à la figure 76 ci-dessous, à lire et interpréter à l’aide des légendes suivantes:</p>
                <table className="annex-table text-center">
                    <thead>
                        <tr>
                            <th scope="col">Note</th>
                            <th scope="col">Tendance par rapport à la cible d’une agriculture productive et durable</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>1 –&lt; 1,5</td>
                            <td>Détérioration</td>
                        </tr>
                        <tr>
                            <td>1,5 –&lt; 2,5</td>
                            <td>Légère détérioration</td>
                        </tr>
                        <tr>
                            <td>2,5 –&lt; 3,5</td>
                            <td>Aucune amélioration</td>
                        </tr>
                        <tr>
                            <td>3,5 –&lt; 4,5</td>
                            <td>Légère amélioration</td>
                        </tr>
                        <tr>
                            <td>4,5 – 5</td>
                            <td>Amélioration</td>
                        </tr>

                    </tbody>
                    <thead>
                        <tr>
                            <th scope="col">Note</th>
                            <th scope="col">État d’avancement actuel par rapport à la cible d’une agriculture productive et durable</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>1 –&lt; 1,5</td>
                            <td>Très éloigné de la cible</td>
                        </tr>
                        <tr>
                            <td>1,5 –&lt; 2,5</td>
                            <td>Éloigné de la cible</td>
                        </tr>
                        <tr>
                            <td>2,5 –&lt; 3,5</td>
                            <td>Proche de la cible</td>
                        </tr>
                        <tr>
                            <td>3,5 –&lt; 4,5</td>
                            <td>Très proche de la cible</td>
                        </tr>
                        <tr>
                            <td>4,5 – 5</td>
                            <td>Cible déjà atteinte</td>
                        </tr>

                    </tbody>
                </table>


                
            </Container>

            <ContainerChart
                title="Figure 75. Progrès accomplis en direction d’une agriculture productive et durable (avec pondération) "
                legend={[
                    { title: 'État d’avancement actuel', color: '#ed7d31' },
                    { title: 'Tendance', color: '#5b9bd5' },
                ]} note={<span>SOURCE: Propres calculs à partir des données de la base de données mondiale des ODD et de FAOSTAT.</span>}>

               <HighchartsReact
                   highcharts={Highcharts}
                   options={figure75}
               />
            </ContainerChart>

            <Container>
                <p>La figure 75 illustre les progrès accomplis en direction d’une agriculture durable aux niveaux mondial et régional, d’après la mesure supplétive. Pour la plupart des régions ainsi que pour l’ensemble du monde, la cible d’une agriculture productive et durable peut être qualifiée de «proche». Elle est même «très proche» dans plusieurs régions, notamment en Océanie, dans les PEID, en Amérique du Nord et en Europe, ainsi qu’en Asie centrale. En revanche, l’ensemble du monde n’a que peu progressé en direction de la cible d’une agriculture productive et durable si l’on compare les derniers chiffres disponibles aux valeurs de référence de 2015, année où le Programme de développement durable à l’horizon 2030 a été adopté. Les progrès accomplis en direction d’une agriculture durable semblent en particulier être à l’arrêt dans beaucoup de pays. Ainsi, par exemple, les régions Asie de l’Ouest et Afrique du Nord, Amérique latine et Caraïbes, et Amérique du Nord et Océanie n’enregistrent aucune amélioration depuis 2015 par rapport à la cible d’une agriculture productive et durable. </p>
                </Container>

            <ContainerChart
                title="Figure 76. Progrès accomplis en direction d’une agriculture durable dans le monde, par dimension thématique (2019)"
                legend={[
                    { title: 'Tendance', color: '#ed7d31' },
                   { title: 'Statut actuel', color: '#5b9bd5' },
               ]} note={<span>SOURCE: Propres calculs à partir des données de la base de données mondiale des ODD et de FAOSTAT.</span>}>

                <HighchartsReact
                    highcharts={Highcharts}
                    options={figure76}
                />
            </ContainerChart>
                
            <Container>
                <p>La figure 76 illustre les progrès accomplis en direction d’une agriculture productive et durable uniquement au niveau mondial, mais par le prisme des huit composantes de l’indicateur supplétif utilisé pour la cible 2.4. Les résultats indiquent que la production brute par unité de surface agricole est la principale pierre d’achoppement pour parvenir à la durabilité globale des exploitations agricoles dans le monde puisqu’elle obtient la note «éloigné de la cible», même si une amélioration importante a été enregistrée ces dernières années. Une analyse combinée de l’état d’avancement actuel et des tendances indique qu’il importe d’accorder davantage d’attention aux épandages de pesticides et à la diversité des productions végétales et animales, où les progrès semblent être à l’arrêt.</p>
                <p>De leur côté, les niveaux de stress hydrique et de pauvreté rurale au niveau mondial semblent être très proches de la cible, et s’en sont relativement bien rapprochés depuis 2015. Ces résultats doivent toutefois être interprétés avec prudence dans l’esprit de «ne laisser personne de côté», car les moyennes mondiales peuvent masquer d’immenses écarts entre les régions. À titre d’exemple, si le stress hydrique peut ne pas apparaître problématique pour l’ensemble du monde, de nombreuses régions sont confrontées à des niveaux de stress hydrique très élevés, voire critiques (voir la section sur l’indicateur 6.4.2). De même, l’ensemble du monde est «très proche de la cible» concernant la pauvreté rurale mais, sur les quelque 120 pays pour lesquels on dispose de données à ce sujet, 40 (soit un tiers) en sont encore loin ou très loin.</p>
            </Container>
            

            <Container>

<Subtitle text="Annexe" />
<Subtitle text="Méthodologie proposée pour l’indicateur supplétif de durabilité de l’agriculture" />
    <p>L’indicateur supplétif proposé pour mesurer le caractère durable de l’agriculture consiste en un ensemble de huit grandeurs établies relatives à la durabilité et à la productivité dans l’agriculture, fondées sur des statistiques nationales largement disponibles qui sont liées en grande partie aux processus établis de communication de statistiques de la FAO; certaines grandeurs portent aussi sur d’autres indicateurs des ODD:</p>
   
                <div className="table-responsive">
    <table className="table">
        <thead>
            <tr>
            <th scope="col">Dimension</th>
            <th scope="col">Thème du sous-indicateur</th>
            <th scope="col">Grandeur supplétive proposée</th>
            <th scope="col">Cible numérique </th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td>Économique </td>
                <td>Productivité de la terre </td>
                <td>Production brute à l’hectare  </td>
                <td>Non</td>
            </tr>
            <tr>
                <td>Économique</td>
                <td>Atténuation des risques pour les agriculteurs </td>
                <td>Diversification de la production brute </td>
                <td>Non </td>
            </tr>
            <tr>
                <td>Environnement </td>
                <td>Qualité des sols </td>
                <td>Consommation d’engrais à l’hectare  </td>
                <td>Oui</td>
            </tr>
            <tr>
                <td>Environnement </td>
                <td>Disponibilité en ressources en eau</td>
                <td>Composante agricole du stress hydrique (désagrégation de l’indicateur 6.4.2)</td>
                <td>Oui</td>
            </tr>
            <tr>
                <td>Environnement </td>
                <td>Gestion des pesticides </td>
                <td>Consommation de pesticides à l’hectare </td>
                <td>Oui</td>
            </tr>
            <tr>
                <td>Environnement </td>
                <td>Pratiques favorisant la biodiversité  </td>
                <td>Proportion de la surface exploitée en agriculture biologique</td>
                <td>Non</td>
            </tr>
            <tr>
                <td>Sociale </td>
                <td>Revenu des agriculteurs </td>
                <td>Valeur ajoutée agricole par travailleur (lien avec l’indicateur 2.3.2) </td>
                <td>Non</td>
            </tr>
            <tr>
                <td>Sociale </td>
                <td>Insécurité alimentaire des agriculteurs </td>
                <td>Proportion de la population rurale vivant en dessous du seuil international de pauvreté (désagrégation de l’indicateur 1.1.1)</td>
                <td>Oui</td>
            </tr>
        </tbody>
    </table>
</div>
                <p>Les huit grandeurs choisies reprennent au maximum les 11 sous-indicateurs de l’indicateur 2.4.1 des ODD, en maintenant un bon équilibre entre les dimensions sociale, économique et environnementale considérées comme les trois piliers du développement durable. Elles sont fondées sur une vaste étude réalisée de façon indépendante par la FAO au cours des deux dernières années, qui a abouti à l’élaboration du cadre d’analyse Progrès accomplis vers une agriculture durable (PROSA) <span className="ref" onClick={() => handleClickOpen('<b>Ignaciuk, A., Ilicic, J., Asprooth, L., Sitko, N.J., Bernard, A., Maggio, G., Tubiello, F.N. &amp; Mueller, M.</b> 2021. Progress towards sustainable agriculture – Drivers of change. FAO Agricultural Development Economics Technical Study No. 13. Rome, FAO. <a href="https://doi.org/10.4060/cb7896en">https://doi.org/10.4060/cb7896en</a>')}>(Ignaciuk <i>et al.</i>, 2021)</span>.</p>
                <p>Contrairement à l’indicateur 2.4.1 des ODD, dont les 11 sous-indicateurs doivent être recueillis au niveau des exploitations agricoles, les données pour les huit grandeurs supplétives sont recueillies et analysées au niveau national. À l’inverse également de l’indicateur 2.4.1 original dont les 11 sous-indicateurs ont chacun leur propre seuil de durabilité pour évaluer leur distance actuelle par rapport à ce niveau, les huit grandeurs supplétives seront évaluées à la fois du point de vue du sens et de la constance de leur évolution, et du point de vue de leur situation actuelle. Cela sera fait conformément à la méthodologie adoptée pour le Tableau récapitulatif des progrès vers les ODD établi au niveau mondial, ainsi que par la FAO elle-même pour le présent rapport <span className="ref" onClick={() => handleClickOpen('<b>Nations Unies.</b> 2022b. Progress Charts. <em>In: Division de la statistique de l’ONU.</em> New York. <a href="https://unstats.un.org/sdgs/report/2021/progress-chart/">https://unstats.un.org/sdgs/report/2021/progress-chart/</a>, dernière consultation le 8 juin 2022.')}>(Nations Unies, 2022)</span>. Sur les huit grandeurs supplétives, une seule est assortie d’une cible numérique clairement définie, et trois autres ont une limite supérieure établie de manière conventionnelle ou scientifique. Cette limite supérieure ne peut toutefois pas servir de cible normative pour les besoins de la présente évaluation des progrès, car les pays qui se trouvent sous la limite supérieure n’ont pas nécessairement besoin de chercher à l’atteindre.</p>
    <p>Par conséquent, les quatre principales méthodes d’évaluation des progrès, concernant la tendance et la situation actuelle pour les indicateurs assortis ou non d’une cible numérique, seront généralement les suivantes:</p>
   
    <table className="table">
    <tbody>
            <tr>
                <td className='darksalmon'>Évaluation de la tendance pour les indicateurs assortis d’une cible numérique: ratio réel par rapport au ratio requis (ratio composé).</td>
                <td className="salmon">Évaluation de la tendance pour les indicateurs sans cible numérique: croissance réelle (taux de croissance annuel composé) comparée à la valeur de référence.</td>
            </tr>
            <tr>
                <td className="ocean">Évaluation de l’état d’avancement pour les indicateurs assortis d’une cible numérique: distance par rapport à la cible.</td>
                <td className="lightocean">Évaluation de l’état d’avancement pour les indicateurs sans cible numérique: répartition par quintile.</td>
            </tr>
    </tbody>
    </table>

<Subtitle text="Traduction de l’évaluation des progrès en une note attribuée à chaque pays" />
    <p>Pour chaque pays, les notes attribuées à chaque sous-indicateur à partir de la méthode décrite à l’annexe 1 et à l’annexe 2 seront moyennées, et la note moyenne déterminera alors le classement du pays dans l’une des cinq plages caractérisant la tendance et la situation actuelle du pays sur le plan de la productivité et de la durabilité de son agriculture:</p>
    
                <table className="table">
                    <thead>
    <tr>
                            <th scope="col">Note</th>
                            <th scope="col">Tendance par rapport à la cible d’une agriculture productive et durable</th>
                        </tr>
                        </thead>

                    <tbody>
                        <tr>
                            <td>1 –&lt; 1,5</td>
                            <td>Plage 1: Détérioration.</td>
                        </tr>
                        <tr>
                            <td>1,5 –&lt; 2,5</td>
                            <td>Plage 2: Légère détérioration.</td>
                        </tr>
                        <tr>
                            <td>2,5 –&lt; 3,5</td>
                            <td>Plage 3: Aucune amélioration.</td>
                        </tr>
                        <tr>
                            <td>3,5 –&lt; 4,5</td>
                            <td>Plage 4: Légère amélioration.</td>
                        </tr>
                        <tr>
                            <td>4,5 – 5</td>
                            <td>Plage 5: Amélioration.</td>
                        </tr>
                      </tbody>  
                      </table>
                      <table className="table">

    </table>
    
                <table className="table">
                    <thead>
    <tr>
                            <th scope="col">Note</th>
                            <th scope="col">Situation actuelle par rapport à la cible d’une agriculture productive et durable</th>
                        </tr>
                        </thead>

                    <tbody>
                        <tr>
                            <td>1 –&lt; 1,5</td>
                            <td>Plage 1: Très éloigné de la cible.</td>
                        </tr>
                        <tr>
                            <td>1,5 –&lt; 2,5</td>
                            <td>Plage 2: Éloigné de la cible.</td>
                        </tr>
                        <tr>
                            <td>2,5 –&lt; 3,5</td>
                            <td>Plage 3: Proche de la cible.</td>
                        </tr>
                        <tr>
                            <td>3,5 –&lt; 4,5</td>
                            <td>Plage 4: Très proche de la cible.</td>
                        </tr>
                        <tr>
                            <td>4,5 – 5</td>
                            <td>Plage 5: Cible déjà atteinte.</td>
                        </tr>
                      </tbody>  
                      </table>

                      <p>Une méthode d’agrégation similaire a déjà été appliquée pour les indicateurs 5.a.2, 14.6.1 et 14.b.1 des ODD.</p>
                      <p>Les deux conditions proposées pour que le calcul soit réalisé (si elles ne sont pas satisfaites, aucune note n’est attribuée) sont:</p>
<p>1) l’existence d’au moins quatre sous-indicateurs sur huit pour le pays; et</p>
<p>2) l’existence d’au moins un sous-indicateur pour chacune des dimensions sociale et économique, et d’au moins deux sous-indicateurs pour la dimension environnementale.</p> 

<Subtitle text="Note régionale globale" />
    <p>Des notes globales régionales seront calculées en faisant la moyenne simple ou pondérée des notes respectives des pays pour la tendance et la situation actuelle. Les régions seront donc classées dans les cinq mêmes plages que les pays, en fonction de leur note globale.</p>
    <p>Les notes obtenues en moyenne simple et en moyenne pondérée ont les unes comme les autres des avantages et des inconvénients importants, raison pour laquelle la FAO propose d’utiliser les deux pour des analyses plus fines. Pour le présent rapport cependant, des moyennes pondérées sont employées pour présenter les résultats mondiaux et les résultats régionaux.</p>
    <p>Avec une moyenne simple, le même poids est attribué à tous les pays pour le calcul de la note globale régionale. De ce fait, l’indicateur est plus sensible aux changements que chaque pays doit apporter à sa politique publique pour progresser vers la cible d’une agriculture productive et durable, et il est donc plus conforme au principe d’universalité du Programme 2030.</p>
    <p>En revanche, une pondération des notes en fonction de la superficie de terres agricoles permet de tenir compte du fait que les pays qui ont une plus grande surface de terres agricoles peuvent produire proportionnellement un impact plus important sur l’agriculture durable à l’échelle de la planète. Dans beaucoup de régions, cela signifie que les pays de plus grande taille pèseront davantage dans le calcul et détermineront en pratique la note régionale globale, réduisant la contribution des pays plus petits.</p>
            </Container>

            <Container>
                <b>Références:</b>
                    <p>
                        <b>FAO.</b> 2022. <em>Methodological documents and supporting material for SDG Indicator 2.4.1. Proportion of agricultural area under productive and sustainable agriculture.</em> Rome, FAO. <a href="https://www.fao.org/3/cb6372en/cb6372en.pdf">https://www.fao.org/3/cb6372en/cb6372en.pdf</a>
                    </p>
                    <p>
                        <b>Ignaciuk, A., Ilicic, J., Asprooth, L., Sitko, N.J., Bernard, A., Maggio, G., Tubiello, F.N. &amp; Mueller, M.</b> 2021. Progress towards sustainable agriculture – Drivers of change. FAO Agricultural Development Economics Technical Study No. 13. Rome, FAO. <a href="https://doi.org/10.4060/cb7896en">https://doi.org/10.4060/cb7896en</a>
                    </p>
                    <p>
                        <b>Nations Unies.</b> 2022b. Progress Charts. <em>In: Division de la statistique de l’ONU.</em> New York. <a href="https://unstats.un.org/sdgs/report/2021/progress-chart/">https://unstats.un.org/sdgs/report/2021/progress-chart/</a>, dernière consultation le 8 juin 2022.
                    </p>
                    <br /><br /><br /><br />
            </Container>
        </React.Fragment>
    ) 
            }

export default MeasuringProductiveSustainableAgriculture;
